/**
 * Transition Effects (Pure Math)
 *
 * Pure functions for computing transition effects.
 * No state, no side effects, easily testable.
 *
 * Transition window: For clips A→B touching at time T with duration D:
 *   - Window spans [T - D/2] to [T + D/2]
 *   - Outgoing (A): fades out from T - D/2 to T
 *   - Incoming (B): fades in from T to T + D/2
 */

import type { TransitionType, TransitionEffect, Transition, ClipTiming } from './types';
import { DEFAULT_EFFECT } from './types';
import { areClipsTouching } from './clipSnapping';

const PLAYHEAD_WINDOW_EPS = 1 / 60; // Full frame at 60fps — prevents boundary flicker

/**
 * Easing function for smooth transitions.
 * Cubic ease-in-out for natural motion.
 */
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Check if playhead is within transition window.
 *
 * @param playhead - Current playhead time
 * @param cutPoint - Where clips touch (clipA.end === clipB.start)
 * @param duration - Transition duration
 * @returns True if in transition window
 */
export function isInTransitionWindow(
  playhead: number,
  cutPoint: number,
  duration: number
): boolean {
  const halfDuration = duration / 2;
  const windowStart = cutPoint - halfDuration;
  const windowEnd = cutPoint + halfDuration;
  return playhead + PLAYHEAD_WINDOW_EPS >= windowStart && playhead <= windowEnd + PLAYHEAD_WINDOW_EPS;
}

/**
 * Calculate transition progress (0 to 1).
 *
 * @param playhead - Current playhead time
 * @param cutPoint - Where clips touch
 * @param duration - Transition duration
 * @returns Progress from 0 (start) to 1 (end)
 */
export function getTransitionProgress(
  playhead: number,
  cutPoint: number,
  duration: number
): number {
  const halfDuration = duration / 2;
  const windowStart = cutPoint - halfDuration;
  const progress = (playhead - windowStart) / duration;
  return Math.max(0, Math.min(1, progress));
}

/**
 * Create a rectangular wipe clip path.
 */
function createWipeClipPath(
  direction: 'left' | 'right' | 'up' | 'down',
  progress: number,
  isOutgoing: boolean,
  canvasW: number,
  canvasH: number
): (ctx: CanvasRenderingContext2D) => void {
  const reveal = isOutgoing ? 1 - progress : progress;

  return (ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    switch (direction) {
      case 'left':
        ctx.rect(canvasW * (1 - reveal), 0, canvasW * reveal, canvasH);
        break;
      case 'right':
        ctx.rect(0, 0, canvasW * reveal, canvasH);
        break;
      case 'up':
        ctx.rect(0, canvasH * (1 - reveal), canvasW, canvasH * reveal);
        break;
      case 'down':
        ctx.rect(0, 0, canvasW, canvasH * reveal);
        break;
    }
    ctx.closePath();
  };
}

/**
 * Create a diagonal wipe clip path.
 */
function createDiagonalWipeClipPath(
  corner: 'tl' | 'tr' | 'bl' | 'br',
  progress: number,
  isOutgoing: boolean,
  canvasW: number,
  canvasH: number
): (ctx: CanvasRenderingContext2D) => void {
  const reveal = isOutgoing ? 1 - progress : progress;
  const expand = reveal * 2; // Over-expand to cover diagonal

  return (ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    switch (corner) {
      case 'tl':
        ctx.moveTo(0, 0);
        ctx.lineTo(canvasW * expand, 0);
        ctx.lineTo(0, canvasH * expand);
        break;
      case 'tr':
        ctx.moveTo(canvasW, 0);
        ctx.lineTo(canvasW * (1 - expand), 0);
        ctx.lineTo(canvasW, canvasH * expand);
        break;
      case 'bl':
        ctx.moveTo(0, canvasH);
        ctx.lineTo(canvasW * expand, canvasH);
        ctx.lineTo(0, canvasH * (1 - expand));
        break;
      case 'br':
        ctx.moveTo(canvasW, canvasH);
        ctx.lineTo(canvasW * (1 - expand), canvasH);
        ctx.lineTo(canvasW, canvasH * (1 - expand));
        break;
    }
    ctx.closePath();
  };
}

/**
 * Create a radial (circle) clip path expanding from center.
 */
function createRadialClipPath(
  progress: number,
  isOutgoing: boolean,
  canvasW: number,
  canvasH: number
): (ctx: CanvasRenderingContext2D) => void {
  const reveal = isOutgoing ? 1 - progress : progress;
  const maxRadius = Math.sqrt(canvasW * canvasW + canvasH * canvasH) / 2;
  const radius = maxRadius * reveal;

  return (ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    ctx.arc(canvasW / 2, canvasH / 2, Math.max(0, radius), 0, Math.PI * 2);
    ctx.closePath();
  };
}

/**
 * Create a clock wipe clip path (pie-shaped reveal).
 */
function createClockWipeClipPath(
  clockwise: boolean,
  progress: number,
  isOutgoing: boolean,
  canvasW: number,
  canvasH: number
): (ctx: CanvasRenderingContext2D) => void {
  const reveal = isOutgoing ? 1 - progress : progress;
  const cx = canvasW / 2;
  const cy = canvasH / 2;
  const maxRadius = Math.sqrt(canvasW * canvasW + canvasH * canvasH);
  const startAngle = -Math.PI / 2;
  const sweep = reveal * Math.PI * 2;
  const endAngle = clockwise ? startAngle + sweep : startAngle - sweep;

  return (ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, maxRadius, startAngle, endAngle, !clockwise);
    ctx.closePath();
  };
}

/**
 * Create a shape-based clip path (morph transitions).
 */
function createShapeClipPath(
  shape: 'circle' | 'diamond' | 'heart' | 'star',
  progress: number,
  isOutgoing: boolean,
  canvasW: number,
  canvasH: number
): (ctx: CanvasRenderingContext2D) => void {
  const reveal = isOutgoing ? 1 - progress : progress;
  const cx = canvasW / 2;
  const cy = canvasH / 2;
  const maxSize = Math.max(canvasW, canvasH) * 1.2;
  const size = maxSize * reveal;

  return (ctx: CanvasRenderingContext2D) => {
    ctx.beginPath();
    switch (shape) {
      case 'circle':
        ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
        break;
      case 'diamond': {
        const half = size / 2;
        ctx.moveTo(cx, cy - half);
        ctx.lineTo(cx + half, cy);
        ctx.lineTo(cx, cy + half);
        ctx.lineTo(cx - half, cy);
        break;
      }
      case 'heart': {
        const s = size / 2;
        ctx.moveTo(cx, cy + s * 0.7);
        ctx.bezierCurveTo(cx - s, cy, cx - s, cy - s * 0.6, cx, cy - s * 0.3);
        ctx.bezierCurveTo(cx + s, cy - s * 0.6, cx + s, cy, cx, cy + s * 0.7);
        break;
      }
      case 'star': {
        const outerR = size / 2;
        const innerR = outerR * 0.4;
        for (let i = 0; i < 10; i++) {
          const r = i % 2 === 0 ? outerR : innerR;
          const angle = (Math.PI / 5) * i - Math.PI / 2;
          const px = cx + r * Math.cos(angle);
          const py = cy + r * Math.sin(angle);
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        break;
      }
    }
    ctx.closePath();
  };
}

/**
 * Compute transition effect for a clip at current playhead.
 *
 * @param type - Transition type
 * @param progress - Progress from 0 to 1
 * @param isOutgoing - True if clip is exiting (A), false if entering (B)
 * @param canvasW - Canvas width in pixels
 * @param canvasH - Canvas height in pixels
 * @returns Computed transition effect
 */
export function computeEffect(
  type: string,
  progress: number,
  isOutgoing: boolean,
  canvasW: number,
  canvasH: number
): TransitionEffect {
  const eased = easeInOutCubic(progress);

  switch (type) {
    // ===== FADES =====
    case 'crossfade':
    case 'dissolve': {
      const opacity = isOutgoing ? Math.sqrt(1 - progress) : Math.sqrt(progress);
      return { ...DEFAULT_EFFECT, opacity };
    }

    case 'fade-black':
    case 'fade-white':
    case 'dip-black':
    case 'dip-white': {
      let opacity: number;
      if (isOutgoing) {
        opacity = progress < 0.5 ? 1 - progress * 2 : 0;
      } else {
        opacity = progress < 0.5 ? 0 : (progress - 0.5) * 2;
      }
      return { ...DEFAULT_EFFECT, opacity };
    }

    // ===== SLIDES =====
    case 'slide-left': {
      const offsetX = isOutgoing ? -eased * canvasW : (1 - eased) * canvasW;
      return { ...DEFAULT_EFFECT, offsetX };
    }

    case 'slide-right': {
      const offsetX = isOutgoing ? eased * canvasW : -(1 - eased) * canvasW;
      return { ...DEFAULT_EFFECT, offsetX };
    }

    case 'slide-up': {
      const offsetY = isOutgoing ? -eased * canvasH : (1 - eased) * canvasH;
      return { ...DEFAULT_EFFECT, offsetY };
    }

    case 'slide-down': {
      const offsetY = isOutgoing ? eased * canvasH : -(1 - eased) * canvasH;
      return { ...DEFAULT_EFFECT, offsetY };
    }

    case 'push-left': {
      const offsetX = isOutgoing ? -eased * canvasW : (1 - eased) * canvasW;
      return { ...DEFAULT_EFFECT, offsetX };
    }

    case 'push-right': {
      const offsetX = isOutgoing ? eased * canvasW : -(1 - eased) * canvasW;
      return { ...DEFAULT_EFFECT, offsetX };
    }

    case 'push-up': {
      const offsetY = isOutgoing ? -eased * canvasH : (1 - eased) * canvasH;
      return { ...DEFAULT_EFFECT, offsetY };
    }

    case 'push-down': {
      const offsetY = isOutgoing ? eased * canvasH : -(1 - eased) * canvasH;
      return { ...DEFAULT_EFFECT, offsetY };
    }

    // ===== WIPES =====
    case 'wipe-left': {
      const clipPath = createWipeClipPath('left', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-right': {
      const clipPath = createWipeClipPath('right', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-up': {
      const clipPath = createWipeClipPath('up', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-down': {
      const clipPath = createWipeClipPath('down', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-clock': {
      const clipPath = createClockWipeClipPath(true, eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-counter-clock': {
      const clipPath = createClockWipeClipPath(false, eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-radial': {
      const clipPath = createRadialClipPath(eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-iris': {
      // Iris = radial but starts smaller and snaps tighter
      const clipPath = createRadialClipPath(eased * eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-diagonal-tl': {
      const clipPath = createDiagonalWipeClipPath('tl', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-diagonal-tr': {
      const clipPath = createDiagonalWipeClipPath('tr', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-diagonal-bl': {
      const clipPath = createDiagonalWipeClipPath('bl', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'wipe-diagonal-br': {
      const clipPath = createDiagonalWipeClipPath('br', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    // ===== ZOOMS =====
    case 'zoom-in': {
      if (isOutgoing) {
        return { ...DEFAULT_EFFECT, scale: 1 + eased * 0.5, opacity: 1 - eased };
      } else {
        return { ...DEFAULT_EFFECT, scale: 0.5 + eased * 0.5, opacity: eased };
      }
    }

    case 'zoom-out': {
      if (isOutgoing) {
        return { ...DEFAULT_EFFECT, scale: 1 - eased * 0.5, opacity: 1 - eased };
      } else {
        return { ...DEFAULT_EFFECT, scale: 1.5 - eased * 0.5, opacity: eased };
      }
    }

    case 'zoom-rotate-in': {
      if (isOutgoing) {
        return { ...DEFAULT_EFFECT, scale: 1 + eased * 0.5, rotation: eased * 90, opacity: 1 - eased };
      } else {
        return { ...DEFAULT_EFFECT, scale: 0.5 + eased * 0.5, rotation: (1 - eased) * -90, opacity: eased };
      }
    }

    case 'zoom-rotate-out': {
      if (isOutgoing) {
        return { ...DEFAULT_EFFECT, scale: 1 - eased * 0.5, rotation: -eased * 90, opacity: 1 - eased };
      } else {
        return { ...DEFAULT_EFFECT, scale: 1.5 - eased * 0.5, rotation: (1 - eased) * 90, opacity: eased };
      }
    }

    // ===== SPIN =====
    case 'spin-in': {
      if (isOutgoing) {
        return { ...DEFAULT_EFFECT, rotation: eased * 360, opacity: 1 - eased, scale: 1 - eased * 0.3 };
      } else {
        return { ...DEFAULT_EFFECT, rotation: (1 - eased) * -360, opacity: eased, scale: 0.7 + eased * 0.3 };
      }
    }

    case 'spin-out': {
      if (isOutgoing) {
        return { ...DEFAULT_EFFECT, rotation: -eased * 360, opacity: 1 - eased, scale: 1 + eased * 0.3 };
      } else {
        return { ...DEFAULT_EFFECT, rotation: (1 - eased) * 360, opacity: eased, scale: 1.3 - eased * 0.3 };
      }
    }

    // ===== MORPH (shape wipes) =====
    case 'morph-circle': {
      const clipPath = createShapeClipPath('circle', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'morph-diamond': {
      const clipPath = createShapeClipPath('diamond', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'morph-heart': {
      const clipPath = createShapeClipPath('heart', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    case 'morph-star': {
      const clipPath = createShapeClipPath('star', eased, isOutgoing, canvasW, canvasH);
      return { ...DEFAULT_EFFECT, clipPath };
    }

    default:
      return DEFAULT_EFFECT;
  }
}

/**
 * Get the transition effect for a clip at the current playhead.
 * This is the main entry point for the transition system.
 *
 * @param clip - The clip to check
 * @param playhead - Current playhead time
 * @param transition - The transition (if any) affecting this clip
 * @param otherClip - The other clip in the transition
 * @param canvasW - Canvas width
 * @param canvasH - Canvas height
 * @returns TransitionEffect to apply, or null if no transition active
 */
export function getClipTransitionEffect(
  clip: ClipTiming,
  playhead: number,
  transition: Transition | null,
  otherClip: ClipTiming | null,
  canvasW: number,
  canvasH: number
): TransitionEffect | null {
  // No transition defined
  if (!transition || !otherClip) {
    return null;
  }

  // Determine if this clip is outgoing or incoming
  const isOutgoing = transition.fromClipId === clip.id;
  const isIncoming = transition.toClipId === clip.id;

  if (!isOutgoing && !isIncoming) {
    return null; // This transition doesn't involve this clip
  }

  // Get the cut point (where clips touch)
  const cutPoint = isOutgoing ? clip.end : clip.start;

  // Check if we're in the transition window
  if (!isInTransitionWindow(playhead, cutPoint, transition.duration)) {
    return null;
  }

  // NOTE: Both clips need effects during the ENTIRE transition window.
  // For spatial transitions (slide, push), incoming clip starts off-screen.
  // For crossfade, incoming starts at opacity 0.
  // For wipes, incoming is clipped.
  // The effect computation handles all positioning/opacity correctly.

  // Calculate progress and compute effect
  const progress = getTransitionProgress(playhead, cutPoint, transition.duration);
  return computeEffect(transition.type, progress, isOutgoing, canvasW, canvasH);
}

/**
 * Check if a clip should be visible at the current playhead.
 * Handles both normal visibility and transition hold windows.
 *
 * During a transition, BOTH clips must be visible:
 * - Outgoing clip: visible from its normal time PLUS after its end (during transition)
 * - Incoming clip: visible from BEFORE its start (during transition) PLUS its normal time
 *
 * @param clip - The clip to check
 * @param playhead - Current playhead time
 * @param transition - Transition affecting this clip (if any)
 * @returns True if clip should be visible
 */
export function isClipVisible(
  clip: ClipTiming,
  playhead: number,
  transition: Transition | null
): boolean {
  // Normal visibility: playhead within clip bounds
  if (playhead + PLAYHEAD_WINDOW_EPS >= clip.start && playhead <= clip.end + PLAYHEAD_WINDOW_EPS) {
    return true;
  }

  // Extended visibility for OUTGOING clip during transition
  // (visible after its end time, during the second half of transition)
  if (transition && transition.fromClipId === clip.id) {
    const cutPoint = clip.end;
    const holdEnd = cutPoint + transition.duration / 2;
    if (playhead + PLAYHEAD_WINDOW_EPS >= cutPoint && playhead <= holdEnd + PLAYHEAD_WINDOW_EPS) {
      return true;
    }
 }

  // NOTE: Incoming clip visibility is now handled at RENDERER level, not here.
  // The renderer forces both clips visible when they have transition effects.
  // This prevents the "early raw frame" bug where clip B appeared unstyled.

  return false;
}

/**
 * Check if playhead is in a gap (no clip covers it).
 * During gaps, only background should render.
 *
 * @param clips - All clips on the track
 * @param playhead - Current playhead time
 * @returns True if in a gap
 */
export function isPlayheadInGap(clips: ClipTiming[], playhead: number): boolean {
  return !clips.some((c) => playhead + PLAYHEAD_WINDOW_EPS >= c.start && playhead <= c.end + PLAYHEAD_WINDOW_EPS);
}

/**
 * Find the transition for a clip (if any).
 *
 * @param clip - The clip to find transition for
 * @param transitions - All transitions
 * @param clips - All clips (to find the other clip in transition)
 * @returns Object with transition and otherClip, or nulls if none
 */
export function findTransitionForClip(
  clip: ClipTiming,
  transitions: Transition[],
  clips: ClipTiming[]
): { transition: Transition | null; otherClip: ClipTiming | null; isOutgoing: boolean } {
  // Check if this clip is the "from" (outgoing) clip
  const outTransition = transitions.find((t) => t.fromClipId === clip.id);
  if (outTransition) {
    const toClip = clips.find((c) => c.id === outTransition.toClipId);
    if (toClip && areClipsTouching(clip, toClip)) {
      return { transition: outTransition, otherClip: toClip, isOutgoing: true };
    }
  }

  // Check if this clip is the "to" (incoming) clip
  const inTransition = transitions.find((t) => t.toClipId === clip.id);
  if (inTransition) {
    const fromClip = clips.find((c) => c.id === inTransition.fromClipId);
    if (fromClip && areClipsTouching(fromClip, clip)) {
      return { transition: inTransition, otherClip: fromClip, isOutgoing: false };
    }
  }

  return { transition: null, otherClip: null, isOutgoing: false };
}
